import type { FunctionComponent } from "react";
import type { AddLexiconTermData, InventoryRequestData } from "@noa/api-core";
import { AddLexiconTermForm, submitAddLexiconTerm } from "./forms/add-lexicon-term-form";
import { InventoryRequestForm, submitInventoryRequest } from "./forms/inventory-request-form";

export interface ActionFormProps<T> {
  data: T;
  onChange: (data: T) => void;
  onSubmit: () => void;
  onCancel: () => void;
  isSubmitting: boolean;
}

export interface ActionConfig<T = Record<string, unknown>> {
  formComponent: FunctionComponent<ActionFormProps<T>>;
  submitHandler: (data: T) => Promise<void>;
  /**
   * Namespace-qualified i18n key for the card's success line, with `{ term }`
   * available for interpolation — e.g. `"lexicon:actionCard.added"`. The
   * namespace is part of the key because the status component renders every
   * action type and binds none.
   *
   * Omit it for actions that only simulate a submit — those keep the default
   * prototype notice, which would misreport an action that really persisted.
   */
  successMessageKey?: string;
}

export const ACTION_REGISTRY: Record<string, ActionConfig> = {};

export function registerAction<T>(type: string, config: ActionConfig<T>): void {
  ACTION_REGISTRY[type] = config as ActionConfig;
}

export function getActionConfig(type: string): ActionConfig | undefined {
  return ACTION_REGISTRY[type];
}

// Register inventory-request action
registerAction<InventoryRequestData>("inventory-request", {
  formComponent: InventoryRequestForm,
  submitHandler: submitInventoryRequest,
});

// Register add-lexicon-term action. Unlike inventory-request, this one really
// writes the concept, so it carries its own success line.
registerAction<AddLexiconTermData>("add-lexicon-term", {
  formComponent: AddLexiconTermForm,
  submitHandler: submitAddLexiconTerm,
  successMessageKey: "lexicon:actionCard.added",
});
